import { useEffect, useRef, useState } from 'react';
import './flow-api-panel.css';
import { flowGuide, type FlowApiSchema } from './flow-api-guide';

const reasons: Record<string, string> = {
  FLOW_API_NOT_CONFIGURED: 'На сервере ещё не настроен ключ Flow API.',
  UNSUPPORTED_GRAPH: 'Граф содержит ноды или связи, которые серверный API пока не выполняет.',
  UNSUPPORTED_MODEL: 'Одна из моделей флоу недоступна серверному API.',
  INVALID_APP_INPUT: 'Отметьте входы кнопкой «+» на ноде. Имена API должны быть уникальны: латинские буквы, цифры и _.',
  REFERENCE_UNAVAILABLE: 'Выберите доступный файл из Assets.',
  MISSING_INPUT: 'Подключите обязательные входы нод.',
  INVALID_PORT: 'Проверьте соединения портов.',
  GRAPH_CYCLE: 'Удалите цикл в графе.',
  UNREACHABLE_NODE: 'Все ноды должны вести к Export.',
  NOT_FOUND: 'Flow недоступен владельцу серверного API или текущему пользователю.'
};
export { flowCurl } from './flow-api-guide';
export function FlowApiPanel({ flowId, beforeOpen, openRequest = 0, showTrigger = true }: { flowId: string; beforeOpen(): Promise<unknown>; openRequest?: number; showTrigger?: boolean }) {
  const [open, setOpen] = useState(false), [schema, setSchema] = useState<FlowApiSchema | null>(null);
  const [input, setInput] = useState<Record<string, string>>({}), [error, setError] = useState('');
  const [loading, setLoading] = useState(false), [copied, setCopied] = useState('');
  const [requestId, setRequestId] = useState('');
  const [hintDismissed, setHintDismissed] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handledOpenRequest = useRef(0);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  const load = async () => {
    clearTimeout(copyTimer.current);
    setOpen(true); setLoading(true); setError(''); setSchema(null); setCopied(''); setHintDismissed(false); setApiKey('');
    try {
      await beforeOpen();
      const response = await fetch(`/api/v1/flows/${flowId}/api-interface`, { cache: 'no-store' });
      const body = await response.json();
      if (!response.ok) throw new Error(reasons[body.code] || `API недоступен: ${body.code || response.status}`);
      const { api_key, ...definition } = body;
      setApiKey(typeof api_key === 'string' ? api_key : '');
      setSchema({ ...definition, flow_id: flowId });
      setInput(Object.fromEntries(body.fields.map((field: any) => [field.name, field.example || 'Example'])));
      setRequestId(crypto.randomUUID());
    } catch (e: any) { setError(e.message || 'Не удалось сохранить Flow или загрузить схему.'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!openRequest || handledOpenRequest.current === openRequest) return;
    handledOpenRequest.current = openRequest;
    void load();
  }, [openRequest]);
  const document = schema ? flowGuide(schema, input, location.origin, requestId, apiKey) : '';
  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      clearTimeout(copyTimer.current);
      setCopied(label); setHintDismissed(false); setError('');
      copyTimer.current = setTimeout(() => setHintDismissed(true), 1000);
    }
    catch { setCopied(''); setError('Не удалось скопировать. Разрешите доступ к буферу обмена и нажмите кнопку ещё раз.'); }
  };
  return <>
    {showTrigger && <button data-flow-api-open="1" className="btn" style={{ position: 'absolute', bottom: 16, left: 16, zIndex: 4 }} onClick={load}>API / Инструкция</button>}
    {open && <aside aria-label="Flow API" style={{ position: 'absolute', zIndex: 10, right: 0, top: 0, bottom: 0, width: 560, maxWidth: '100%', overflow: 'auto', padding: 20, background: 'var(--card, #fff)', color: 'var(--ink, #111)', borderLeft: '1px solid var(--hairline, #ddd)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}><h3>Подключить Flow по API</h3><button className="btn" aria-label="Закрыть Flow API" onClick={() => setOpen(false)}>×</button></div>
      {loading && <p role="status">Сохраняем Flow и проверяем интерфейс…</p>}
      {error && <p role="alert">{error}</p>}
      {schema && <>
        <div className="flow-api-instructions">
          <p className="flow-api-intro">Запускайте генерацию прямо из чата с вашим агентом.</p>
          <ol className="flow-api-steps">
            <li className="flow-api-step-copy"><span className="flow-api-step-number" aria-hidden="true">1</span><div><strong>Скопируйте инструкцию</strong><p>Посмотрите, что получит агент: здесь описаны запуск API и входы этого Flow.</p></div>
              <div className="flow-api-code-panel">
                <div className="flow-api-code-header"><span>Инструкция для агента · Markdown</span><span className={`flow-api-copy-control${copied ? ' is-copied' : ''}${hintDismissed ? ' is-dismissed' : ''}`} onMouseEnter={() => { setCopied(''); setHintDismissed(false); }} onFocus={() => { setCopied(''); setHintDismissed(false); }}><button className="btn flow-api-copy" aria-describedby="flow-api-copy-hint" onClick={() => copy(document, 'Инструкция скопирована')}>Копировать</button><span id="flow-api-copy-hint" className="flow-api-copy-hint" role="tooltip">{copied || 'Нажмите, чтобы скопировать'}</span><span className="flow-api-copy-status" role="status" aria-live="polite">{copied}</span></span></div>
                <pre aria-label="Текст инструкции для агента"><code>{document}</code></pre>
              </div>
            </li>
            <li><span className="flow-api-step-number" aria-hidden="true">2</span><div><strong>Передайте её агенту</strong><p>Вставьте инструкцию в чат с вашим агентом и отправьте сообщение.</p></div></li>
            <li><span className="flow-api-step-number" aria-hidden="true">3</span><div><strong>Опишите, что хотите создать</strong><p>Агент подскажет, какие данные нужны, и поможет запустить генерацию.</p></div></li>
          </ol>
        </div>
      </>}
    </aside>}
  </>;
}
